// Customer portal — login screen. This is the PRIMARY way in.
//
// Email + temp/chosen password → POST /api/customer/login, which sets the
// HttpOnly `kos_customer` session cookie. On success we hand control back to
// the shell via `onAuthenticated()` so it can load + render the project.
//
// Onboarding is temp-password, not magic-link (Steve, 2026-08-12): the wizard,
// CRM add-customer and quote-send paths all email a temporary password via the
// shared `buildWelcomeEmail`. Magic links (`?token=`) still WORK — the shell
// honours them and "Resend portal link" mints one — but they are a convenience
// on top, not the route a first-time customer is expected to take, so the
// first-time hint at the bottom of this card describes the password instead.
//
// Visual design ported from the legacy single-file portal's login card
// (kitchenos/customer-standalone.html:502-536 + its CSS at :57). The
// legacy form was a *demo* gate (password-only, hardcoded demo email);
// here it's a real email+password login. The forgot-password reveal is
// kept like-for-like (POST /api/customer/forgot-password).

import { api, ApiError } from "@tkos/api-client";

interface LoginOptions {
  /** Called after a successful login (session cookie is set). */
  onAuthenticated: () => void;
}

/** Pull a friendly message out of an ApiError whose body is `{ok,error}`. */
function friendlyError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    try {
      const parsed = JSON.parse(err.message) as { error?: string };
      if (parsed && typeof parsed.error === "string" && parsed.error) {
        return parsed.error;
      }
    } catch {
      /* message wasn't JSON — fall through */
    }
    // 401s are auth failures regardless of body shape.
    if (err.status === 401) return "Incorrect email or password.";
  }
  return fallback;
}

/** Inline brand glyph — custom 24×24, 1.5px stroke (no icon library). */
function brandGlyph(): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.5");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("width", "26");
  svg.setAttribute("height", "26");
  for (const d of ["M3 10.5 12 4l9 6.5", "M5 9.5V20h14V9.5", "M9.5 20v-5h5v5"]) {
    const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
    p.setAttribute("d", d);
    svg.appendChild(p);
  }
  return svg;
}

export function renderLogin(rootEl: HTMLElement, opts: LoginOptions): void {
  rootEl.replaceChildren();

  const wrap = document.createElement("div");
  wrap.className = "cust-login";

  const card = document.createElement("div");
  card.className = "cust-login-card";

  // ── Brand mark ──────────────────────────────────────────────────────
  const brand = document.createElement("div");
  brand.className = "cust-login-brand";
  const logo = document.createElement("div");
  logo.className = "cust-login-logo";
  logo.appendChild(brandGlyph());
  const name = document.createElement("div");
  name.className = "cust-login-name";
  // The homeowner-facing product name, NOT the platform brand. Must match the
  // manifest short_name / apple-mobile-web-app-title ("Kitchen Portal") — this
  // is the screen they install the icon from, so naming it anything else sends
  // them looking for an app they'll never find. The platform takes its credit
  // in the "Powered by Kitchengine" footer at the bottom of this card instead.
  name.textContent = "Kitchen Portal";
  // No sub-line: "Kitchen Portal" over "Customer Portal" said the same thing
  // twice (Steve, 2026-08-20). This was the only user of `.cust-login-sub`, so
  // that rule in styles.css is now dead — left in place rather than deleted in
  // the same change, since it is one selector and removing CSS is a separate
  // risk from removing markup.
  brand.append(logo, name);

  const heading = document.createElement("h1");
  heading.className = "cust-login-heading";
  heading.textContent = "Welcome back";

  const blurb = document.createElement("p");
  blurb.className = "cust-login-blurb";
  blurb.textContent =
    "Sign in to track your kitchen project, view designs and quotes, and stay in touch with your designer.";

  // ── Login form ──────────────────────────────────────────────────────
  const form = document.createElement("form");
  form.className = "cust-login-form";
  form.noValidate = true;

  const emailLabel = document.createElement("label");
  emailLabel.htmlFor = "cust-login-email";
  emailLabel.textContent = "Email";
  const emailInput = document.createElement("input");
  emailInput.id = "cust-login-email";
  emailInput.type = "email";
  emailInput.autocomplete = "email";
  emailInput.placeholder = "you@email.com";
  emailInput.required = true;

  const pwLabel = document.createElement("label");
  pwLabel.htmlFor = "cust-login-password";
  pwLabel.textContent = "Password";
  const pwInput = document.createElement("input");
  pwInput.id = "cust-login-password";
  pwInput.type = "password";
  pwInput.autocomplete = "current-password";
  pwInput.placeholder = "Your password";
  pwInput.required = true;

  const submit = document.createElement("button");
  submit.type = "submit";
  submit.className = "cust-login-primary";
  submit.textContent = "Sign in";

  const err = document.createElement("div");
  err.className = "cust-login-err";
  err.setAttribute("role", "alert");
  err.setAttribute("aria-live", "polite");

  // Retailer picker — shown ONLY when one email+password matches accounts under
  // more than one retailer. Tenant-isolation-safe disambiguation: the server
  // never guesses which tenant to sign into; the customer chooses.
  const picker = document.createElement("div");
  picker.className = "cust-login-picker";
  picker.hidden = true;
  const pickerHint = document.createElement("div");
  pickerHint.className = "cust-login-picker-hint";
  pickerHint.textContent =
    "This email is registered with more than one retailer. Choose which one to sign in to.";
  picker.append(pickerHint);

  form.append(emailLabel, emailInput, pwLabel, pwInput, submit, err, picker);

  type LoginOption = { accountId: string; retailerName: string };
  function showRetailerPicker(choiceToken: string, options: LoginOption[]): void {
    for (const el of [emailLabel, emailInput, pwLabel, pwInput, submit]) el.hidden = true;
    picker.hidden = false;
    picker.querySelectorAll(".cust-login-retailer-option").forEach((n) => n.remove());
    for (const opt of options) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "cust-login-retailer-option";
      b.textContent = opt.retailerName;
      b.addEventListener("click", () => {
        err.textContent = "";
        picker.querySelectorAll("button").forEach((n) => ((n as HTMLButtonElement).disabled = true));
        api<{ ok: boolean; error?: string }>("/customer/login/select", {
          method: "POST",
          body: JSON.stringify({ choiceToken, accountId: opt.accountId }),
        })
          .then((d) => {
            if (!d.ok) {
              err.textContent = d.error || "Sign-in failed.";
              picker.querySelectorAll("button").forEach((n) => ((n as HTMLButtonElement).disabled = false));
              return;
            }
            opts.onAuthenticated();
          })
          .catch((e2) => {
            err.textContent = friendlyError(e2, "Couldn't sign in. Please try again.");
            picker.querySelectorAll("button").forEach((n) => ((n as HTMLButtonElement).disabled = false));
          });
      });
      picker.append(b);
    }
  }

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const email = emailInput.value.trim().toLowerCase();
    const password = pwInput.value;
    err.textContent = "";
    if (!email || !password) {
      err.textContent = "Please enter your email and password.";
      return;
    }
    submit.disabled = true;
    submit.textContent = "Signing in…";
    api<{
      ok: boolean;
      error?: string;
      needsRetailerChoice?: boolean;
      choiceToken?: string;
      options?: LoginOption[];
    }>("/customer/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    })
      .then((d) => {
        // Ambiguous email (registered with >1 retailer): show the picker
        // instead of erroring; no session is set until the customer chooses.
        if (d.ok && d.needsRetailerChoice && d.choiceToken && Array.isArray(d.options)) {
          submit.textContent = "Sign in";
          showRetailerPicker(d.choiceToken, d.options);
          return;
        }
        if (!d.ok) {
          err.textContent = d.error || "Sign-in failed.";
          submit.disabled = false;
          submit.textContent = "Sign in";
          return;
        }
        opts.onAuthenticated();
      })
      .catch((e2) => {
        err.textContent = friendlyError(e2, "Couldn't sign in. Please try again.");
        submit.disabled = false;
        submit.textContent = "Sign in";
      });
  });

  // ── Forgot password (ported like-for-like) ──────────────────────────
  const forgotToggle = document.createElement("button");
  forgotToggle.type = "button";
  forgotToggle.className = "cust-login-link";
  forgotToggle.textContent = "Forgot password?";

  const forgot = document.createElement("div");
  forgot.className = "cust-login-forgot";
  forgot.hidden = true;

  const forgotHint = document.createElement("div");
  forgotHint.className = "cust-login-forgot-hint";
  forgotHint.textContent = "Enter your account email to receive a reset link.";

  const forgotEmail = document.createElement("input");
  forgotEmail.type = "email";
  forgotEmail.autocomplete = "email";
  forgotEmail.placeholder = "you@email.com";
  forgotEmail.className = "cust-login-forgot-email";

  const forgotSend = document.createElement("button");
  forgotSend.type = "button";
  forgotSend.className = "cust-login-primary";
  forgotSend.textContent = "Send reset link";

  const forgotMsg = document.createElement("div");
  forgotMsg.className = "cust-login-forgot-msg";
  forgotMsg.setAttribute("aria-live", "polite");

  const forgotBack = document.createElement("button");
  forgotBack.type = "button";
  forgotBack.className = "cust-login-link";
  forgotBack.textContent = "Back to sign in";

  forgot.append(forgotHint, forgotEmail, forgotSend, forgotMsg, forgotBack);

  forgotToggle.addEventListener("click", () => {
    forgot.hidden = false;
    forgotToggle.hidden = true;
    forgotEmail.value = emailInput.value.trim();
    forgotEmail.focus();
  });
  forgotBack.addEventListener("click", () => {
    forgot.hidden = true;
    forgotToggle.hidden = false;
    forgotMsg.textContent = "";
  });
  forgotSend.addEventListener("click", () => {
    const email = forgotEmail.value.trim().toLowerCase();
    forgotMsg.textContent = "";
    forgotMsg.classList.remove("is-error");
    if (!email) {
      forgotMsg.textContent = "Please enter your email.";
      forgotMsg.classList.add("is-error");
      return;
    }
    forgotSend.disabled = true;
    forgotSend.textContent = "Sending…";
    // Neutral confirmation regardless of whether the email is registered
    // (avoids account enumeration) — mirrors the legacy behaviour.
    api<{ ok: boolean }>("/customer/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    })
      .catch(() => {
        /* swallow — we still show the neutral confirmation below */
      })
      .finally(() => {
        forgotMsg.textContent = "Reset link sent if that email is registered.";
        forgotSend.textContent = "Sent";
      });
  });

  // ── First-time hint ─────────────────────────────────────────────────
  // Copy matches the ACTUAL onboarding mechanism: every path that creates a
  // customer (lead-gen wizard, CRM add-customer, quote send, "Resend portal
  // link") emails a temporary password — see the module header. The previous
  // wording promised "it signs you in automatically", describing the
  // magic-link design retired on 2026-08-12, and sent first-time customers
  // hunting for an auto-sign-in link that no longer exists.
  const hint = document.createElement("p");
  hint.className = "cust-login-hint";
  hint.textContent =
    "First time here? Use the email address and temporary password in your welcome email. " +
    "You can change the password once you're signed in.";

  const foot = document.createElement("div");
  foot.className = "cust-login-foot";
  const footStrong = document.createElement("span");
  footStrong.className = "cust-login-foot-brand";
  footStrong.textContent = "Kitchengine";
  foot.append(document.createTextNode("Powered by "), footStrong);

  card.append(brand, heading, blurb, form, forgotToggle, forgot, hint, foot);
  wrap.appendChild(card);
  rootEl.appendChild(wrap);

  setTimeout(() => emailInput.focus(), 30);
}
